/**
 * iter137 — Global axios interceptor: auto-report client-visible API errors
 * to the AI Error Monitor pipeline so admin sees them in <5min instead of
 * waiting for user feedback.
 *
 * Behaviour:
 *   • Reports ALL responses with HTTP status >= 500 as severity='high'
 *   • Reports 422 (Pydantic validation errors) as severity='medium' since
 *     these usually mean a frontend ⇄ backend schema drift (the bug class
 *     that crashed the Quiz admin save in iter136).
 *   • Throttles to 1 report per signature per 60s (client-side) to avoid
 *     storms when an endpoint is down.
 *   • Skips reports for the report endpoint itself (avoid recursion).
 *   • Skips noisy network-cancelled requests (axios CanceledError).
 *
 * Install once at app boot from index.js or App.js.
 */
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const _seen = new Map();           // signature → last_sent_at_ms
const THROTTLE_MS = 60_000;

function _shouldSkip(error) {
  if (!error) return true;
  if (axios.isCancel?.(error)) return true;
  const url = error.config?.url || '';
  if (url.includes('/api/errors/report')) return true;
  // No response → genuine network error, browser will already surface it.
  if (!error.response) return true;
  const st = error.response.status;
  return st < 422 || (st !== 422 && st < 500);
}

function _signature(error) {
  const cfg = error.config || {};
  const url = (cfg.url || '').replace(/\/\d+/g, '/:id').slice(0, 120);
  const status = error.response?.status || 0;
  return `${cfg.method || 'GET'} ${url} ${status}`;
}

function _stringifyDetail(detail) {
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(d => {
      if (typeof d === 'string') return d;
      const loc = Array.isArray(d?.loc) ? d.loc.filter(x => x !== 'body').join('.') : '';
      const msg = d?.msg || d?.message || 'invalid';
      return loc ? `${loc}: ${msg}` : msg;
    }).join(' · ');
  }
  if (typeof detail === 'object' && detail) return JSON.stringify(detail).slice(0, 500);
  return '';
}

export function installAxiosErrorReporter() {
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      try {
        if (_shouldSkip(error)) throw error;
        const sig = _signature(error);
        const now = Date.now();
        const last = _seen.get(sig);
        if (last && now - last < THROTTLE_MS) throw error;
        _seen.set(sig, now);

        const status = error.response?.status || 0;
        const detail = _stringifyDetail(error.response?.data?.detail || error.response?.data);
        const url = error.config?.url || '';
        const method = (error.config?.method || 'GET').toUpperCase();
        const message = `${method} ${url} → ${status}${detail ? ' · ' + detail : ''}`.slice(0, 2000);
        const severity = status >= 500 ? 'high' : 'medium';
        const module = `api/${url.replace(/^.*\/api\//, '').split('?')[0].split('/').slice(0, 3).join('/').slice(0, 70) || 'unknown'}`;

        const payload = {
          source: 'frontend',
          module,
          message,
          stack: '',           // network errors don't have one — keep slim
          severity,
          url: window.location.href.slice(0, 500),
          user_agent: navigator.userAgent.slice(0, 255),
          http_status: status,
          request_id: error.response?.headers?.['x-request-id'] || '',
        };

        // Fire & forget, never block the original error from propagating.
        const body = JSON.stringify(payload);
        try {
          if (navigator.sendBeacon) {
            const blob = new Blob([body], { type: 'application/json' });
            navigator.sendBeacon(`${API}/api/errors/report`, blob);
          } else {
            fetch(`${API}/api/errors/report`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body,
              credentials: 'include',
              keepalive: true,
            }).catch(() => {});
          }
        } catch { /* never let reporting break the app */ }
      } catch { /* fall through to reject */ }
      return Promise.reject(error);
    },
  );
}

/**
 * iter246a-1 — Bearer fallback interceptor.
 *
 * In production we rely on httpOnly cookies set by `/api/auth/login`.
 * But cookie delivery silently fails in a few environments:
 *   • Safari with strict ITP (cross-site cookies blocked)
 *   • Some Android/iOS WebViews (in-app browsers)
 *   • Playwright/test contexts where the cookie store isn't synced
 *
 * As a safety net we attach `Authorization: Bearer <localStorage.access_token>`
 * on every same-origin request whenever it's missing. Backend's get_current_user
 * already accepts either cookie OR Bearer header, so this is a pure additive
 * fallback — when cookies do flow through (the happy path) nothing changes.
 */
export function installBearerFallback() {
  if (typeof window === 'undefined') return;
  axios.interceptors.request.use((config) => {
    try {
      const url = String(config?.url || '');
      // Only attach for our own backend.
      const isOurApi = url.startsWith('/api/') || (API && url.startsWith(API));
      if (!isOurApi) return config;
      // iter254b — also accept the canonical 'japap_auth_token' key (the
      // permanent-session token name) in addition to the legacy 'access_token'.
      const t = localStorage.getItem('access_token') || localStorage.getItem('japap_auth_token');
      if (!t) return config;
      // axios v1 uses AxiosHeaders, but it also exposes .set() / supports
      // direct property assignment. We try both to stay compatible.
      const headers = config.headers || {};
      const hasAuth = Boolean(
        (typeof headers.get === 'function' && headers.get('Authorization'))
        || headers.Authorization || headers.authorization,
      );
      if (hasAuth) return config;
      if (typeof headers.set === 'function') {
        headers.set('Authorization', `Bearer ${t}`);
      } else {
        headers.Authorization = `Bearer ${t}`;
      }
      config.headers = headers;
    } catch { /* never let interceptor break a request */ }
    return config;
  });

  // iter254b — Response interceptor: translate backend i18n error keys
  // (auth.required, auth.session_expired, upload.file_too_large, ...) into
  // the user's current language so toast/snackbar messages are localised.
  // The backend now returns *only* keys for these failure modes — see
  // /app/backend/routes/auth.py get_current_user.
  const KNOWN_KEYS = new Set([
    'auth.required', 'auth.unauthorized', 'auth.token_invalid',
    'auth.session_expired', 'auth.session_revoked', 'auth.user_not_found',
    'auth.logout_success',
    'errors.server_error',
    'upload.file_too_large', 'upload.invalid_format', 'upload.invalid_id',
    'upload.invalid_chunk_index', 'upload.chunk_too_large',
    'upload.session_not_found', 'upload.no_chunks',
  ]);
  axios.interceptors.response.use(
    (r) => r,
    (error) => {
      try {
        const detail = error?.response?.data?.detail;
        if (typeof detail === 'string' && KNOWN_KEYS.has(detail)) {
          // Lazy import — avoids hard-dep on i18next at module load.
          // eslint-disable-next-line global-require
          const i18n = require('@/i18n').default;
          const translated = i18n?.t?.(detail);
          if (translated && translated !== detail) {
            // Mutate the response payload so the caller's toast picks it up.
            error.response.data.detail = translated;
            error.response.data.message_key = detail;
          }
        }
      } catch { /* never let interceptor swallow the error */ }
      return Promise.reject(error);
    },
  );
}


export default installAxiosErrorReporter;
